/**
 * ReseauParametresPage — Paramètres réseau (Lot R5).
 * Abonnement Premium (19 € HT/mois, Stripe checkout + polling), visibilité
 * du profil avec consentement SEO explicite, préférences de notifications.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Settings, Crown, Eye, Bell, Loader2, CheckCircle2, XCircle,
} from 'lucide-react';
import api from '../../lib/api';
import ReseauLayout from '../../components/reseau/ReseauLayout';

const VISIBILITY_OPTIONS = [
    { value: 'hidden', label: 'Masqué', desc: 'Votre profil public est désactivé.' },
    { value: 'linkonly', label: 'Lien direct uniquement', desc: 'Visible seulement par ceux qui ont votre lien.' },
    { value: 'indexable', label: 'Moteur Homly', desc: 'Les vendeurs vous trouvent dans la recherche Homly.' },
    { value: 'seo_indexable', label: 'Moteur Homly + Google', desc: 'Votre profil peut apparaître sur Google (consentement requis).' },
];

const NOTIF_CATEGORIES = [
    { key: 'opportunites', label: 'Opportunités', desc: 'Nouvelles demandes vendeurs, réponses attendues.' },
    { key: 'reputation', label: 'Réputation', desc: 'Nouveaux avis, preuves validées.' },
    { key: 'reseau', label: 'Réseau', desc: 'Nouveaux contacts, interactions utiles.' },
    { key: 'progression', label: 'Progression', desc: 'Étapes de complétude, recommandations.' },
];

function Section({ title, icon: Icon, children, id }) {
    return (
        <section className="reseau-card p-5" id={id}>
            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider mb-4"
                style={{ color: 'var(--reseau-ink-muted)' }}>
                <Icon className="w-4 h-4" style={{ color: 'var(--reseau-violet)' }} />
                {title}
            </h2>
            {children}
        </section>
    );
}

export default function ReseauParametresPage() {
    const [user, setUser] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const [paymentState, setPaymentState] = useState(null); // checking | success | error | expired
    const [seoConsent, setSeoConsent] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const pollCount = useRef(0);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const [me, s] = await Promise.all([api.get('/auth/me'), api.get('/reseau/settings')]);
            setUser(me.data);
            setSettings(s.data);
        } catch (e) {
            if (e?.response?.status === 401) navigate('/login?next=/reseau/parametres');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => { load(); }, [load]);

    // Retour de Stripe : polling du statut de paiement
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (!sessionId) return;
        setPaymentState('checking');
        const poll = async () => {
            if (pollCount.current >= 6) { setPaymentState('error'); return; }
            pollCount.current += 1;
            try {
                const res = await api.get(`/reseau/premium/checkout/status/${sessionId}`);
                if (res.data.payment_status === 'paid') {
                    setPaymentState('success');
                    setSearchParams({}, { replace: true });
                    await load();
                    return;
                }
                if (res.data.status === 'expired') { setPaymentState('expired'); return; }
                setTimeout(poll, 2000);
            } catch {
                setPaymentState('error');
            }
        };
        poll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const subscribe = async () => {
        setSubscribing(true);
        try {
            const res = await api.post('/reseau/premium/checkout',
                { origin_url: window.location.origin });
            window.location.href = res.data.url;
        } catch {
            setSubscribing(false);
            setPaymentState('error');
        }
    };

    const cancelPremium = async () => {
        await api.post('/reseau/premium/cancel', { reason: '' });
        await load();
    };

    const setVisibility = async (level) => {
        try {
            await api.patch('/reseau/settings/visibility',
                { level, seo_consent: level === 'seo_indexable' ? seoConsent : undefined });
            await load();
        } catch { /* 422 si consentement manquant : l'UI le force déjà */ }
    };

    const toggleNotif = async (key, value) => {
        const res = await api.patch('/reseau/settings/notifications', { [key]: value });
        setSettings((s) => ({ ...s, notification_prefs: res.data.notification_prefs }));
    };

    const premium = settings?.premium;

    return (
        <ReseauLayout user={user} headerTitle="Paramètres">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="reseau-parametres-page">
                <header>
                    <h1 className="flex items-center gap-2 text-xl font-bold" style={{ color: 'var(--reseau-ink)' }}>
                        <Settings className="w-5 h-5" style={{ color: 'var(--reseau-violet)' }} />
                        Paramètres réseau
                    </h1>
                </header>

                {loading && (
                    <div className="flex justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--reseau-violet)' }} />
                    </div>
                )}

                {!loading && settings && (
                    <>
                        <Section title="Abonnement Premium" icon={Crown} id="premium">
                            {paymentState === 'checking' && (
                                <div className="flex items-center gap-2 mb-4 text-sm" data-testid="payment-checking"
                                     style={{ color: 'var(--reseau-ink-muted)' }}>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Vérification du paiement en cours…
                                </div>
                            )}
                            {paymentState === 'success' && (
                                <div className="flex items-center gap-2 mb-4 text-sm font-semibold" data-testid="payment-success"
                                     style={{ color: 'var(--reseau-success, #16835A)' }}>
                                    <CheckCircle2 className="w-4 h-4" />
                                    Paiement confirmé — bienvenue dans Homly Réseau Premium !
                                </div>
                            )}
                            {(paymentState === 'error' || paymentState === 'expired') && (
                                <div className="flex items-center gap-2 mb-4 text-sm" data-testid="payment-error"
                                     style={{ color: 'var(--reseau-danger)' }}>
                                    <XCircle className="w-4 h-4" />
                                    {paymentState === 'expired'
                                        ? 'La session de paiement a expiré. Réessayez.'
                                        : 'Le paiement n\'a pas pu être vérifié. Contactez-nous si vous avez été débité.'}
                                </div>
                            )}

                            {premium?.active ? (
                                <div data-testid="premium-active-block">
                                    <p className="text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>
                                        Premium actif
                                        {premium.status === 'canceled' && ' (résilié — actif jusqu\'à la fin de la période)'}
                                    </p>
                                    <p className="text-xs mt-1" style={{ color: 'var(--reseau-ink-muted)' }}>
                                        {premium.current_period_end &&
                                            `Période en cours jusqu'au ${new Date(premium.current_period_end).toLocaleDateString('fr-FR')}.`}
                                    </p>
                                    {premium.status !== 'canceled' && (
                                        <button onClick={cancelPremium}
                                                className="mt-3 text-xs underline"
                                                style={{ color: 'var(--reseau-ink-muted)' }}
                                                data-testid="premium-cancel-btn">
                                            Résilier (reste actif jusqu'à la fin de la période payée)
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div data-testid="premium-subscribe-block">
                                    <p className="text-sm mb-1" style={{ color: 'var(--reseau-ink)' }}>
                                        <span className="font-bold">Homly Réseau Premium</span> — 19 € HT / mois, sans engagement.
                                    </p>
                                    <ul className="text-xs space-y-1 mb-4" style={{ color: 'var(--reseau-ink-muted)' }}>
                                        <li>• Statistiques détaillées : provenance, entonnoir, zones, rapport mensuel</li>
                                        <li>• CV de carrière complet + export PDF professionnel</li>
                                        <li>• Outils d'avis et de preuves avancés</li>
                                    </ul>
                                    <p className="text-[11px] mb-3" style={{ color: 'var(--reseau-ink-muted)' }}>
                                        Le premium améliore vos outils, jamais votre classement :
                                        la pertinence des recherches reste identique pour tous.
                                    </p>
                                    <button onClick={subscribe} disabled={subscribing}
                                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white disabled:opacity-60"
                                            style={{ background: 'var(--reseau-violet)' }}
                                            data-testid="premium-subscribe-btn">
                                        {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                                        S'abonner — 19 € HT/mois
                                    </button>
                                </div>
                            )}
                        </Section>

                        <Section title="Visibilité de mon profil" icon={Eye} id="visibilite">
                            <div className="space-y-2" data-testid="visibility-options">
                                {VISIBILITY_OPTIONS.map((opt) => {
                                    const active = settings.visibility_level === opt.value;
                                    return (
                                        <label key={opt.value}
                                               className="flex items-start gap-3 p-3 rounded-xl cursor-pointer border transition-colors"
                                               style={{
                                                   background: active ? 'var(--reseau-violet-soft)' : 'var(--reseau-bg-muted)',
                                                   borderColor: active ? 'var(--reseau-violet)' : 'transparent',
                                               }}
                                               data-testid={`visibility-${opt.value}`}>
                                            <input type="radio" name="visibility" className="mt-1"
                                                   checked={active}
                                                   onChange={() => setVisibility(opt.value)} />
                                            <span>
                                                <span className="block text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>
                                                    {opt.label}
                                                </span>
                                                <span className="block text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                                                    {opt.desc}
                                                </span>
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                            <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer"
                                   style={{ color: 'var(--reseau-ink-muted)' }}>
                                <input type="checkbox" checked={seoConsent}
                                       onChange={(e) => setSeoConsent(e.target.checked)}
                                       data-testid="seo-consent-checkbox" />
                                J'autorise explicitement l'indexation de mon profil par les moteurs
                                de recherche externes (Google). Requis pour le niveau « + Google ».
                            </label>
                            {settings.seo_consent_at && (
                                <p className="text-[11px] mt-2" style={{ color: 'var(--reseau-ink-muted)' }}>
                                    Consentement SEO donné le {new Date(settings.seo_consent_at).toLocaleDateString('fr-FR')}.
                                </p>
                            )}
                        </Section>

                        <Section title="Notifications" icon={Bell} id="notifications">
                            <div className="space-y-3" data-testid="notif-prefs">
                                {NOTIF_CATEGORIES.map((cat) => (
                                    <label key={cat.key} className="flex items-center justify-between gap-3 cursor-pointer">
                                        <span>
                                            <span className="block text-sm font-semibold" style={{ color: 'var(--reseau-ink)' }}>
                                                {cat.label}
                                            </span>
                                            <span className="block text-xs" style={{ color: 'var(--reseau-ink-muted)' }}>
                                                {cat.desc}
                                            </span>
                                        </span>
                                        <input type="checkbox"
                                               checked={settings.notification_prefs[cat.key]}
                                               onChange={(e) => toggleNotif(cat.key, e.target.checked)}
                                               data-testid={`notif-toggle-${cat.key}`} />
                                    </label>
                                ))}
                            </div>
                        </Section>
                    </>
                )}
            </div>
        </ReseauLayout>
    );
}
